import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Grid, Chip, CircularProgress,
    IconButton, TextField, Avatar, Switch, FormControlLabel, Badge, Divider,
    MenuItem,
} from '@mui/material';
import {
    Close as CloseIcon,
    Edit as EditIcon,
    PersonOff as PersonOffIcon,
    Message as MessageIcon,
    DirectionsCar as CarIcon,
    LockReset as LockResetIcon,
    FiberManualRecord as StatusDot,
    PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/adminService';
import { userAPI, driversAPI, dispatchersAPI } from '../services/apiService';
import { useAlert } from '../contexts/AlertContext';
import { formatDate } from '../utils/dateHelpers';
import { formatPhoneNumber } from '../config/appConstants';
import CarManager from './CarManager';
import DriverMessagingModal from './DriverMessagingModal';

const GENDER_OPTIONS = ['Male', 'Female', 'Non-binary', 'Other', 'Prefer not to say'];

const InfoRow = ({ label, value }) =>
    value != null && value !== '' ? (
        <Box mb={1.5}>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    ) : null;

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mt: 2, mb: 0.5 }}>
        {children}
    </Typography>
);

// Edit field helper – renders a controlled TextField
const EF = ({ label, value, onChange, type = 'text', required, error, inputProps, InputLabelProps, select, children, size = 'small' }) => (
    <TextField
        fullWidth
        label={label}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        type={type}
        required={required}
        error={!!error}
        helperText={error || ''}
        size={size}
        margin="dense"
        inputProps={inputProps}
        InputLabelProps={InputLabelProps}
        select={select}
    >
        {children}
    </TextField>
);

// Inline photo picker used in edit mode
const PhotoPicker = ({ photoPreview, existingPhotoUrl, onSelect, onClear, label = 'Profile Photo' }) => {
    const inputRef = useRef(null);
    const displaySrc = photoPreview || existingPhotoUrl;
    return (
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 1.5 }}>
            <Avatar
                src={displaySrc}
                sx={{ width: 64, height: 64, border: '2px solid', borderColor: displaySrc ? 'primary.main' : 'divider', cursor: 'pointer', flexShrink: 0 }}
                onClick={() => inputRef.current?.click()}
                imgProps={{ onError: e => { e.target.style.display = 'none'; } }}
            />
            <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{label}</Typography>
                <Box display="flex" gap={1}>
                    <Button size="small" variant="outlined" startIcon={<PhotoCameraIcon />} onClick={() => inputRef.current?.click()}>
                        {displaySrc ? 'Change' : 'Upload'}
                    </Button>
                    {displaySrc && (
                        <Button size="small" color="error" variant="outlined" onClick={onClear}>Remove</Button>
                    )}
                </Box>
            </Box>
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                style={{ display: 'none' }}
                onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) onSelect(file);
                    e.target.value = '';
                }}
            />
        </Box>
    );
};

export default function EmployeeDetailDialog({
    open,
    onClose,
    employee,           // Basic row data from the list
    employeeType,       // 'driver' | 'dispatcher'
    readOnly = false,   // true for fired workers – hides action buttons
    openMessagingModal,
    onMarkMessagesRead,
    onNavigateToRideHistory,
    unreadCount = 0,
    onRefresh,
    onPhotoUpdated,
}) {
    const { showAlert, showToast } = useAlert();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [fireConfirmOpen, setFireConfirmOpen] = useState(false);
    const [showCars, setShowCars] = useState(false);
    const [showMessaging, setShowMessaging] = useState(false);
    const phoneInputRef = useRef(null);

    // Photo edit state
    const [editPhotoFile, setEditPhotoFile] = useState(null);
    const [editPhotoPreview, setEditPhotoPreview] = useState(null);
    const [removeExistingPhoto, setRemoveExistingPhoto] = useState(false);
    const [photoKey, setPhotoKey] = useState(() => Date.now());

    useEffect(() => {
        if (!open || !employee) return;
        setIsEditing(false);
        setValidationErrors({});
        setDetails(null);
        setLoading(true);
        loadDetails();
    }, [open, employee?.id]);

    const loadDetails = async () => {
        try {
            let data;
            if (employeeType === 'driver') {
                data = await adminAPI.drivers.getById(employee.id);
            } else {
                data = await adminAPI.dispatchers.getById(employee.id);
            }
            setDetails(data);
        } catch {
            showToast('Failed to load details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        const d = details;
        if (employeeType === 'dispatcher') {
            setEditData({
                id: d.id,
                name: d.name || '',
                email: d.email || '',
                phoneNumber: d.phoneNumber || '',
                isActive: d.isActive ?? true,
                isAdmin: d.isAdmin ?? false,
            });
        } else {
            setEditData({
                id: d.id,
                name: d.name || '',
                email: d.email || '',
                phoneNumber: d.phoneNumber || '',
                licenseNumber: d.licenseNumber || '',
                userName: d.userName || '',
                dateOfBirth: d.dateOfBirth ? d.dateOfBirth.substring(0, 10) : '',
                gender: d.gender || '',
                addressStreet: d.addressStreet || '',
                addressCity: d.addressCity || '',
                addressState: d.addressState || '',
                addressZip: d.addressZip || '',
                emergencyContactName: d.emergencyContactName || '',
                emergencyContactPhone: d.emergencyContactPhone || '',
                licenseState: d.licenseState || '',
                licenseExpiration: d.licenseExpiration ? d.licenseExpiration.substring(0, 10) : '',
                yearsDrivingExperience: d.yearsDrivingExperience != null ? d.yearsDrivingExperience.toString() : '',
                languagesSpoken: d.languagesSpoken || '',
                preferredWorkHours: d.preferredWorkHours || '',
                insuranceCarrier: d.insuranceCarrier || '',
                insurancePolicyNumber: d.insurancePolicyNumber || '',
                insuranceExpiration: d.insuranceExpiration ? d.insuranceExpiration.substring(0, 10) : '',
            });
        }
        setValidationErrors({});
        setIsEditing(true);
    };

    const handleEditCancel = () => {
        setIsEditing(false);
        setValidationErrors({});
        if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview);
        setEditPhotoFile(null);
        setEditPhotoPreview(null);
        setRemoveExistingPhoto(false);
    };

    const handleEditChange = (field, value) => {
        let processed = value;
        if (field === 'phoneNumber') {
            const digits = value.replace(/\D/g, '');
            processed = formatPhoneNumber(digits);
        }
        setEditData(prev => ({ ...prev, [field]: processed }));
        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateEdit = () => {
        const errors = {};
        if (!editData.name?.trim()) errors.name = 'Required';
        if (!editData.email?.trim()) errors.email = 'Required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editData.email)) errors.email = 'Invalid email';
        const digits = (editData.phoneNumber || '').replace(/\D/g, '');
        if (digits.length !== 10) errors.phoneNumber = 'Valid 10-digit phone required';
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleUpdate = async () => {
        if (!validateEdit()) return;
        setSubmitting(true);
        try {
            if (employeeType === 'driver') {
                const driverPayload = {
                    ...editData,
                    dateOfBirth: editData.dateOfBirth || null,
                    licenseExpiration: editData.licenseExpiration || null,
                    insuranceExpiration: editData.insuranceExpiration || null,
                    yearsDrivingExperience: editData.yearsDrivingExperience !== '' && editData.yearsDrivingExperience != null
                        ? Number(editData.yearsDrivingExperience)
                        : null,
                };
                await adminAPI.drivers.update(driverPayload);
                if (editPhotoFile) {
                    try {
                        await driversAPI.uploadDocument(editData.id, 'ProfilePhoto', editPhotoFile);
                        setPhotoKey(Date.now());
                        onPhotoUpdated?.();
                    } catch {
                        showToast('Profile saved, but photo upload failed.', 'warning');
                    }
                } else if (removeExistingPhoto) {
                    try {
                        await driversAPI.deleteDocument(editData.id, 'ProfilePhoto');
                        setPhotoKey(Date.now());
                        onPhotoUpdated?.();
                    } catch {
                        showToast('Profile saved, but photo removal failed.', 'warning');
                    }
                }
            } else {
                await adminAPI.dispatchers.update(editData);
                if (editPhotoFile) {
                    try {
                        await dispatchersAPI.uploadPhoto(editData.id, editPhotoFile);
                        setPhotoKey(Date.now());
                        onPhotoUpdated?.();
                    } catch {
                        showToast('Profile saved, but photo upload failed.', 'warning');
                    }
                } else if (removeExistingPhoto) {
                    try {
                        await dispatchersAPI.deletePhoto(editData.id);
                        setPhotoKey(Date.now());
                        onPhotoUpdated?.();
                    } catch {
                        showToast('Profile saved, but photo removal failed.', 'warning');
                    }
                }
            }
            showToast('Saved successfully', 'success');
            setIsEditing(false);
            if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview);
            setEditPhotoFile(null);
            setEditPhotoPreview(null);
            setRemoveExistingPhoto(false);
            await loadDetails();
        } catch (err) {
            const msg = err?.response?.data?.error;
            if (msg?.toLowerCase().includes('username')) {
                setValidationErrors(prev => ({ ...prev, userName: msg }));
            } else {
                showToast('Failed to save changes', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleResetPassword = async () => {
        await showAlert(
            'Reset Password',
            `Reset password for ${details?.name}? A new password will be sent to their email.`,
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Reset Password',
                    onPress: async () => {
                        try {
                            await userAPI.forgotPassword(details.id, employeeType === 'driver' ? 'driver' : 'dispatcher');
                            showToast('Password reset. New password sent to their email.', 'success');
                        } catch {
                            showAlert('Error', 'Failed to reset password', [{ text: 'OK' }], 'error');
                        }
                    }
                },
            ],
            'warning'
        );
    };

    const handleFireConfirm = async () => {
        try {
            if (employeeType === 'driver') {
                const result = await adminAPI.drivers.fire(details.id);
                if (result?.affectedCalls > 0) {
                    showToast(`Fired. ${result.affectedCalls} call(s) reassigned.`, 'success');
                } else {
                    showToast('Driver fired successfully.', 'success');
                }
            } else {
                await adminAPI.dispatchers.fire(details.id);
                showToast('Dispatcher fired successfully.', 'success');
            }
            setFireConfirmOpen(false);
            onClose();
            onRefresh?.();
        } catch {
            showAlert('Error', 'Failed to fire. Please try again.', [{ text: 'OK' }], 'error');
        }
    };

    const handleMessage = () => {
        if (onMarkMessagesRead) onMarkMessagesRead(details.id);
        if (openMessagingModal) {
            openMessagingModal(details.id);
        } else {
            setShowMessaging(true);
        }
    };


    const profilePhotoUrl = details
        ? (employeeType === 'driver'
            ? driversAPI.getDocumentUrl(details.id, 'ProfilePhoto', photoKey)
            : dispatchersAPI.getPhotoUrl(details.id, photoKey))
        : null;

    // ── View mode content ───────────────────────────────────────────────────────
    const renderView = () => {
        if (!details) return null;
        if (employeeType === 'dispatcher') {
            return (
                <Box>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Avatar
                            src={profilePhotoUrl}
                            sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 28 }}
                            imgProps={{ onError: e => { e.target.style.display = 'none'; } }}
                        >
                            {details.name?.charAt(0)}
                        </Avatar>
                        <Box>
                            <Typography variant="h6">{details.name}</Typography>
                            <Box display="flex" gap={1} mt={0.5}>
                                {details.isActive && <Chip label="Active" color="success" size="small" />}
                                {details.isAdmin && <Chip label="Admin" color="primary" size="small" />}
                            </Box>
                        </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <InfoRow label="Username" value={details.userName} />
                            <InfoRow label="Email" value={details.email} />
                            <InfoRow label="Phone" value={details.phoneNumber} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow label="Date Joined" value={formatDate(details.dateJoined || details.DateJoined)} />
                            <InfoRow label="End Date" value={formatDate(details.endDate)} />
                        </Grid>
                    </Grid>
                </Box>
            );
        }

        // Driver view
        return (
            <Grid container spacing={3}>
                {/* Column 1 — identity */}
                <Grid item xs={12} md={4}>
                    <Box mb={2} display="flex" justifyContent="center">
                        <Avatar
                            src={profilePhotoUrl}
                            sx={{ width: 96, height: 96, border: '2px solid', borderColor: 'primary.main', fontSize: 36 }}
                            imgProps={{ onError: e => { e.target.style.display = 'none'; } }}
                        >
                            {details.name?.charAt(0)}
                        </Avatar>
                    </Box>

                    <SectionTitle>Personal Information</SectionTitle>
                    <InfoRow label="Name" value={details.name} />
                    <InfoRow label="Username" value={details.userName} />
                    <InfoRow label="Email" value={details.email} />
                    <InfoRow label="Phone" value={details.phoneNumber} />
                    <InfoRow label="Date of Birth" value={details.dateOfBirth ? formatDate(details.dateOfBirth) : ''} />
                    <InfoRow label="Gender" value={details.gender} />
                    <InfoRow label="Date Joined" value={formatDate(details.joinedDate || details.dateJoined)} />
                </Grid>

                {/* Column 2 — location & contacts */}
                <Grid item xs={12} md={4}>
                    {(details.addressStreet || details.addressCity) && (
                        <>
                            <SectionTitle>Address</SectionTitle>
                            <InfoRow label="Street" value={details.addressStreet} />
                            <InfoRow label="City" value={details.addressCity} />
                            <InfoRow label="State / Zip" value={[details.addressState, details.addressZip].filter(Boolean).join(' ')} />
                        </>
                    )}

                    {(details.emergencyContactName || details.emergencyContactPhone) && (
                        <>
                            <SectionTitle>Emergency Contact</SectionTitle>
                            <InfoRow label="Name" value={details.emergencyContactName} />
                            <InfoRow label="Phone" value={details.emergencyContactPhone} />
                        </>
                    )}
                </Grid>

                {/* Column 3 — credentials & work info */}
                <Grid item xs={12} md={4}>
                    {(details.licenseNumber || details.licenseState) && (
                        <>
                            <SectionTitle>Driver's License</SectionTitle>
                            <InfoRow label="License #" value={details.licenseNumber} />
                            <InfoRow label="State" value={details.licenseState} />
                            <InfoRow label="Expires" value={formatDate(details.licenseExpiration)} />
                        </>
                    )}

                    {(details.yearsDrivingExperience != null || details.languagesSpoken || details.preferredWorkHours) && (
                        <>
                            <SectionTitle>Driving Profile</SectionTitle>
                            <InfoRow label="Years Experience" value={details.yearsDrivingExperience?.toString()} />
                            <InfoRow label="Languages" value={details.languagesSpoken} />
                            <InfoRow label="Preferred Hours" value={details.preferredWorkHours} />
                        </>
                    )}

                    {(details.insuranceCarrier || details.insurancePolicyNumber) && (
                        <>
                            <SectionTitle>Insurance</SectionTitle>
                            <InfoRow label="Carrier" value={details.insuranceCarrier} />
                            <InfoRow label="Policy #" value={details.insurancePolicyNumber} />
                            <InfoRow label="Expires" value={formatDate(details.insuranceExpiration)} />
                        </>
                    )}
                </Grid>
            </Grid>
        );
    };

    // ── Edit mode content ───────────────────────────────────────────────────────
    const renderEdit = () => {
        if (employeeType === 'dispatcher') {
            return (
                <Box>
                    <PhotoPicker
                        photoPreview={editPhotoPreview}
                        existingPhotoUrl={removeExistingPhoto ? null : profilePhotoUrl}
                        onSelect={file => {
                            setEditPhotoFile(file);
                            setRemoveExistingPhoto(false);
                            if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview);
                            setEditPhotoPreview(URL.createObjectURL(file));
                        }}
                        onClear={() => {
                            if (editPhotoPreview) {
                                URL.revokeObjectURL(editPhotoPreview);
                                setEditPhotoPreview(null);
                                setEditPhotoFile(null);
                            } else {
                                setRemoveExistingPhoto(true);
                            }
                        }}
                    />
                    <EF label="Name" value={editData.name} onChange={v => handleEditChange('name', v)} required error={validationErrors.name} />
                    <EF label="Email" value={editData.email} onChange={v => handleEditChange('email', v)} type="email" required error={validationErrors.email} />
                    <EF label="Phone" value={editData.phoneNumber} onChange={v => handleEditChange('phoneNumber', v)} type="tel" required error={validationErrors.phoneNumber} inputProps={{ maxLength: 14 }} />
                    <Box sx={{ mt: 1.5, display: 'flex', gap: 3 }}>
                        <FormControlLabel
                            control={<Switch checked={editData.isActive ?? true} onChange={e => handleEditChange('isActive', e.target.checked)} />}
                            label="Active"
                        />
                        <FormControlLabel
                            control={<Switch checked={editData.isAdmin ?? false} onChange={e => handleEditChange('isAdmin', e.target.checked)} />}
                            label="Admin"
                        />
                    </Box>
                </Box>
            );
        }

        // Driver edit
        return (
            <Box>
                <PhotoPicker
                    photoPreview={editPhotoPreview}
                    existingPhotoUrl={removeExistingPhoto ? null : profilePhotoUrl}
                    onSelect={file => {
                        setEditPhotoFile(file);
                        setRemoveExistingPhoto(false);
                        if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview);
                        setEditPhotoPreview(URL.createObjectURL(file));
                    }}
                    onClear={() => {
                        if (editPhotoPreview) {
                            URL.revokeObjectURL(editPhotoPreview);
                            setEditPhotoPreview(null);
                            setEditPhotoFile(null);
                        } else {
                            setRemoveExistingPhoto(true);
                        }
                    }}
                />

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <EF label="Name" value={editData.name} onChange={v => handleEditChange('name', v)} required error={validationErrors.name} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <EF label="Email" value={editData.email} onChange={v => handleEditChange('email', v)} type="email" required error={validationErrors.email} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <EF label="Phone" value={editData.phoneNumber} onChange={v => handleEditChange('phoneNumber', v)} type="tel" required error={validationErrors.phoneNumber} inputProps={{ maxLength: 14 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <EF label="Username" value={editData.userName} onChange={v => handleEditChange('userName', v.toLowerCase().replace(/\s/g, ''))} error={validationErrors.userName} inputProps={{ maxLength: 50 }} />
                    </Grid>
                </Grid>

                <SectionTitle>Personal Information</SectionTitle>
                <EF label="Date of Birth" value={editData.dateOfBirth} onChange={v => handleEditChange('dateOfBirth', v)} type="date" InputLabelProps={{ shrink: true }} />
                <EF label="Gender" value={editData.gender} onChange={v => handleEditChange('gender', v)} select size="medium">
                    <MenuItem value=""><em>Select…</em></MenuItem>
                    {GENDER_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </EF>

                <SectionTitle>Address</SectionTitle>
                <EF label="Street" value={editData.addressStreet} onChange={v => handleEditChange('addressStreet', v)} />
                <Grid container spacing={1}>
                    <Grid item xs={5}>
                        <EF label="City" value={editData.addressCity} onChange={v => handleEditChange('addressCity', v)} />
                    </Grid>
                    <Grid item xs={4}>
                        <EF label="State" value={editData.addressState} onChange={v => handleEditChange('addressState', v.toUpperCase())} inputProps={{ maxLength: 2 }} />
                    </Grid>
                    <Grid item xs={3}>
                        <EF label="Zip" value={editData.addressZip} onChange={v => handleEditChange('addressZip', v)} inputProps={{ maxLength: 10 }} />
                    </Grid>
                </Grid>

                <SectionTitle>Emergency Contact</SectionTitle>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <EF label="Name" value={editData.emergencyContactName} onChange={v => handleEditChange('emergencyContactName', v)} />
                    </Grid>
                    <Grid item xs={6}>
                        <EF label="Phone" value={editData.emergencyContactPhone} onChange={v => handleEditChange('emergencyContactPhone', v)} />
                    </Grid>
                </Grid>

                <SectionTitle>License Information</SectionTitle>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <EF label="License Number" value={editData.licenseNumber} onChange={v => handleEditChange('licenseNumber', v)} error={validationErrors.licenseNumber} inputProps={{ maxLength: 15 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <EF label="License State" value={editData.licenseState} onChange={v => handleEditChange('licenseState', v.toUpperCase())} inputProps={{ maxLength: 2 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <EF label="License Expiration" value={editData.licenseExpiration} onChange={v => handleEditChange('licenseExpiration', v)} type="date" InputLabelProps={{ shrink: true }} />
                    </Grid>
                </Grid>

                <SectionTitle>Driving Profile</SectionTitle>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <EF label="Years Driving Experience" value={editData.yearsDrivingExperience} onChange={v => handleEditChange('yearsDrivingExperience', v)} type="number" inputProps={{ min: 0, max: 60 }} />
                    </Grid>
                    <Grid item xs={6}>
                        <EF label="Languages Spoken" value={editData.languagesSpoken} onChange={v => handleEditChange('languagesSpoken', v)} />
                    </Grid>
                    <Grid item xs={12}>
                        <EF label="Preferred Work Hours" value={editData.preferredWorkHours} onChange={v => handleEditChange('preferredWorkHours', v)} />
                    </Grid>
                </Grid>

                <SectionTitle>Insurance</SectionTitle>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <EF label="Insurance Carrier" value={editData.insuranceCarrier} onChange={v => handleEditChange('insuranceCarrier', v)} />
                    </Grid>
                    <Grid item xs={6}>
                        <EF label="Policy Number" value={editData.insurancePolicyNumber} onChange={v => handleEditChange('insurancePolicyNumber', v)} />
                    </Grid>
                    <Grid item xs={6}>
                        <EF label="Insurance Expiration" value={editData.insuranceExpiration} onChange={v => handleEditChange('insuranceExpiration', v)} type="date" InputLabelProps={{ shrink: true }} />
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={() => { if (!submitting) onClose(); }}
                maxWidth="md"
                fullWidth
                scroll="paper"
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography variant="h6" fontWeight={700}>
                            {loading ? 'Loading…' : (details?.name || employee?.name)}
                        </Typography>
                        {!loading && employeeType === 'driver' && employee?.isAvailable != null && (
                            <Chip
                                icon={<StatusDot sx={{ fontSize: 12 }} />}
                                label={employee.isAvailable ? 'Online' : 'Offline'}
                                size="small"
                                color={employee.isAvailable ? 'success' : 'default'}
                                variant={employee.isAvailable ? 'filled' : 'outlined'}
                            />
                        )}
                    </Box>
                    <IconButton onClick={onClose} size="small" disabled={submitting}><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                    ) : !details ? (
                        <Typography color="error">Failed to load employee details.</Typography>
                    ) : isEditing ? renderEdit() : renderView()}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    {!loading && details && (
                        isEditing ? (
                            <Box display="flex" gap={1} width="100%" justifyContent="flex-end">
                                <Button onClick={handleEditCancel} color="inherit" disabled={submitting}>Cancel</Button>
                                <Button onClick={handleUpdate} variant="contained" disabled={submitting}>
                                    {submitting ? <CircularProgress size={18} /> : 'Update'}
                                </Button>
                            </Box>
                        ) : !readOnly ? (
                            <Box display="flex" gap={1} flexWrap="wrap" width="100%" alignItems="center">
                                <Button
                                    onClick={handleResetPassword}
                                    color="warning"
                                    variant="outlined"
                                    size="small"
                                    startIcon={<LockResetIcon />}
                                >
                                    Reset Password
                                </Button>
                                <Button
                                    onClick={handleEdit}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EditIcon />}
                                >
                                    Edit
                                </Button>
                                {employeeType === 'driver' && (
                                    <>
                                        <Badge
                                            badgeContent={unreadCount || 0}
                                            color="error"
                                            invisible={!unreadCount}
                                        >
                                            <Button
                                                onClick={handleMessage}
                                                color="success"
                                                variant="outlined"
                                                size="small"
                                                startIcon={<MessageIcon />}
                                            >
                                                Message
                                            </Button>
                                        </Badge>
                                        <Button
                                            onClick={() => setShowCars(true)}
                                            color="info"
                                            variant="outlined"
                                            size="small"
                                            startIcon={<CarIcon />}
                                        >
                                            Manage Cars
                                        </Button>
                                    </>
                                )}
                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                    onClick={() => setFireConfirmOpen(true)}
                                    color="error"
                                    variant="contained"
                                    size="small"
                                    startIcon={<PersonOffIcon />}
                                >
                                    Fire
                                </Button>
                            </Box>
                        ) : null
                    )}
                </DialogActions>
            </Dialog>

            {/* Fire Confirmation Dialog */}
            <Dialog open={fireConfirmOpen} onClose={() => setFireConfirmOpen(false)}>
                <DialogTitle>Confirm Fire {employeeType === 'driver' ? 'Driver' : 'Dispatcher'}</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to fire <strong>{details?.name}</strong>?
                    </Typography>
                    {employeeType === 'driver' && (
                        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                            <Typography component="li" variant="body2" color="text.secondary">
                                Set their end date and prevent login
                            </Typography>
                            <Typography component="li" variant="body2" color="text.secondary">
                                Reassign all active calls to other drivers
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" color="error" sx={{ mt: 1.5, fontWeight: 'bold' }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFireConfirmOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleFireConfirm} color="error" variant="contained">
                        Fire {employeeType === 'driver' ? 'Driver' : 'Dispatcher'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cars Dialog */}
            {showCars && details && (
                <Dialog open={showCars} onClose={() => setShowCars(false)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Cars — {details.name}</Typography>
                        <IconButton onClick={() => setShowCars(false)} size="small"><CloseIcon /></IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <CarManager
                            driverId={details.id}
                            driverName={details.name}
                            onClose={() => setShowCars(false)}
                            dialogMode
                        />
                    </DialogContent>
                </Dialog>
            )}

            {/* Messaging Modal (fallback when no openMessagingModal prop) */}
            {showMessaging && details && (
                <DriverMessagingModal
                    isOpen={showMessaging}
                    onClose={() => setShowMessaging(false)}
                    driverId={details.id}
                    driverName={details.name}
                    onNavigateToRideHistory={onNavigateToRideHistory}
                />
            )}
        </>
    );
}
